import React, { useState, useRef, useCallback, useEffect } from 'react';
import type { DragEvent, FC, TouchEvent } from 'react';
import styles from './dragAndRearrange.module.scss';
import SNSListItem from 'pages/MyReviewEdit/components/SNSListItem';
import type { UserProfileSns } from 'store/apiTypes';
import { cx } from '@travel/utils';

type Props = {
  list: UserProfileSns[];
  setUpdatedList: (newList: UserProfileSns[]) => void;
};

const DragAndRearrange: FC<Props> = ({ list, setUpdatedList }) => {
  const [items, setItems] = useState(list);
  const dragItem = useRef<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = (
    e: DragEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>,
    index: number,
  ) => {
    dragItem.current = index;
    setIsDragging(true);
  };

  const handleDragEnter = useCallback(
    (e: DragEvent<HTMLDivElement>, index: number) => {
      if (dragItem.current === null || dragItem.current === index) {
        return;
      }

      const copyListItems = [...items];
      const dragItemContent = copyListItems[dragItem.current];

      copyListItems.splice(dragItem.current, 1);
      copyListItems.splice(index, 0, dragItemContent);

      setItems(copyListItems);
      setUpdatedList(copyListItems);

      dragItem.current = index;
    },
    [items, setUpdatedList],
  );

  const handleDragEnd = () => {
    dragItem.current = null;
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleTouchStart = (e: TouchEvent<HTMLDivElement>, index: number) => {
    handleDragStart(e, index);
  };

  const handleTouchMove = useCallback(
    (ev) => {
      if (dragItem.current === null) return;

      ev.preventDefault();
      const touch = ev.touches[0];
      const target = document.elementFromPoint(touch.clientX, touch.clientY);

      const itemElement = target?.closest(`.${styles.item}`);

      if (itemElement) {
        const parentElement = itemElement.parentNode as HTMLElement | null;

        if (!parentElement) return;

        const targetIndex = Array.from(parentElement.children).indexOf(itemElement);

        if (targetIndex !== -1 && targetIndex !== dragItem.current) {
          handleDragEnter({} as React.DragEvent<HTMLDivElement>, targetIndex);
        }
      }
    },
    [handleDragEnter],
  );

  const handleTouchEnd = () => {
    handleDragEnd();
  };

  useEffect(() => {
    document.addEventListener('touchmove', handleTouchMove as EventListener, { passive: false });

    return () => {
      document.removeEventListener('touchmove', handleTouchMove as EventListener);
    };
  }, [handleTouchMove]);

  return (
    <>
      {items.map((item, index) => (
        <div
          key={item.platform}
          className={cx(
            styles.item,
            isDragging && dragItem.current === index ? styles.dragging : '',
          )}
          draggable
          onDragStart={(e) => handleDragStart(e, index)}
          onDragEnter={(e) => handleDragEnter(e, index)}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onTouchStart={(e) => handleTouchStart(e, index)}
          onTouchEnd={handleTouchEnd}
        >
          <SNSListItem snsItem={item} />
        </div>
      ))}
    </>
  );
};

export default DragAndRearrange;
